/**
 * Normalise product names for UI display.
 *
 * Retailers frequently ship product names in ALL CAPS ("APPLE IPHONE 17 PRO
 * 256GB BLUE"), which reads as shouting in a customer-facing UI. This helper
 * converts uppercase names to Title Case while preserving:
 *   - brand acronyms (LG, HP, IBM, LEGO, ASUS, MSI, JVC, TCL, ...)
 *   - stylised brand casing (iPhone, iPad, MacBook, AirPods, PlayStation, ...)
 *   - spec suffixes (256GB, 1TB, 4K, 60Hz, ...)
 *   - model codes with digits+letters+hyphens (WH-1000XM5, SM-G991, ...)
 *   - small connector words (in, for, and, the, of, ...)
 *
 * Names that are already mixed-case are returned unchanged (we don't mess with
 * names retailers cased correctly). Very short strings (<=3 chars, like "PS5"
 * or "LG") are also returned unchanged.
 *
 * Examples:
 *   "APPLE IPHONE 17 PRO 256GB BLUE"     -> "Apple iPhone 17 Pro 256GB Blue"
 *   "SAMSUNG GALAXY S24 ULTRA 512GB"     -> "Samsung Galaxy S24 Ultra 512GB"
 *   "SONY WH-1000XM5 WIRELESS HEADPHONES"-> "Sony WH-1000XM5 Wireless Headphones"
 *   "LEGO STAR WARS SET 75355"           -> "LEGO Star Wars Set 75355"
 *   "MACBOOK AIR M3 8GB 256GB SILVER"    -> "MacBook Air M3 8GB 256GB Silver"
 *   "iPhone 17 Pro (Blue) 256GB"         -> "iPhone 17 Pro (Blue) 256GB" (unchanged)
 *   "Nintendo Switch OLED 64GB White"    -> "Nintendo Switch OLED 64GB White" (unchanged)
 */

// Brand names / acronyms that must stay UPPERCASE
const UPPERCASE_BRANDS = new Set([
  "LG", "HP", "GE", "IBM", "LEGO", "JVC", "TCL", "ASUS", "MSI",
  "AMD", "GPU", "CPU", "SSD", "HDD", "OLED", "QLED", "LED", "LCD",
  "UHD", "HDR", "USB", "HDMI", "RGB", "ANC", "NFC", "GPS",
  "AI", "AR", "VR", "XR",
  "PS5", "PS4", "PS3",
  "DJI",
]);

// Brands with specific stylised casing (key = uppercase form for matching)
const MIXED_CASE_BRANDS: Record<string, string> = {
  IPHONE: "iPhone",
  IPAD: "iPad",
  IOS: "iOS",
  MACOS: "macOS",
  TVOS: "tvOS",
  WATCHOS: "watchOS",
  MACBOOK: "MacBook",
  IMAC: "iMac",
  IPOD: "iPod",
  AIRPODS: "AirPods",
  AIRTAG: "AirTag",
  AIRPLAY: "AirPlay",
  HOMEPOD: "HomePod",
  PLAYSTATION: "PlayStation",
  PSVR: "PSVR",
  XBOX: "Xbox",
  YOUTUBE: "YouTube",
  WHATSAPP: "WhatsApp",
  WIFI: "WiFi",
  EBAY: "eBay",
  ETSY: "Etsy",
  MYER: "Myer",
  BUNNINGS: "Bunnings",
  KMART: "Kmart",
  KOGAN: "Kogan",
  BECEXTECH: "BecexTech",
};

// Small connector words that stay lowercase (unless they are the first word)
const SMALL_WORDS = new Set([
  "in", "for", "and", "the", "on", "at", "with", "of", "to", "a", "an",
  "or", "by", "as", "vs", "via", "from",
]);

// Spec suffixes like 256GB / 1TB / 60Hz / 4K / 12MP
const SPEC_SUFFIX = /^(\d+)(GB|TB|MB|KB|K|MP|HZ|MHZ|GHZ|W|V|MA|MAH|G|CM|MM|M|KG|IN|FT|PX)$/i;

function isAllUppercase(s: string): boolean {
  // At least one uppercase letter, no lowercase letters. Digits/punctuation ok.
  return /[A-Z]/.test(s) && !/[a-z]/.test(s);
}

function prettifyWord(word: string, isFirst: boolean): string {
  // Strip parens/quotes for classification, remember to reattach.
  const openMatch = word.match(/^[(\[{"'`]+/);
  const closeMatch = word.match(/[)\]}"'`,.:;!?]+$/);
  const open = openMatch ? openMatch[0] : "";
  const close = closeMatch ? closeMatch[0] : "";
  const core = word.slice(open.length, word.length - close.length);

  const styled = styleCore(core, isFirst && !open);
  return `${open}${styled}${close}`;
}

function styleCore(core: string, isFirst: boolean): string {
  if (!core) return core;

  // Known uppercase brands
  if (UPPERCASE_BRANDS.has(core)) return core;

  // Known mixed-case brands
  if (MIXED_CASE_BRANDS[core]) return MIXED_CASE_BRANDS[core];

  // Spec suffix (256GB, 60Hz, 4K)
  const specMatch = core.match(SPEC_SUFFIX);
  if (specMatch) {
    return `${specMatch[1]}${specMatch[2].toUpperCase()}`;
  }

  // Model code: letters+digits+hyphens (WH-1000XM5, SM-G991, X-T5)
  if (/[A-Z]/.test(core) && /\d/.test(core) && /-/.test(core)) {
    return core;
  }

  // Pure digits
  if (/^\d+$/.test(core)) return core;

  // Alpha-only starting with letter + digits (S24, M3, A15) — keep first letter cap, rest as-is
  if (/^[A-Z]\d+$/.test(core)) return core.charAt(0) + core.slice(1);

  // Small connector words (not if first word)
  if (!isFirst && SMALL_WORDS.has(core.toLowerCase())) {
    return core.toLowerCase();
  }

  // Standard title case
  return core.charAt(0).toUpperCase() + core.slice(1).toLowerCase();
}

export function prettifyProductName(raw: string | null | undefined): string {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";

  // Preserve already-mixed-case names and very short tokens ("PS5", "LG").
  if (trimmed.length <= 3) return trimmed;
  if (!isAllUppercase(trimmed)) return trimmed;

  const words = trimmed.split(/\s+/);
  return words.map((w, i) => prettifyWord(w, i === 0)).join(" ");
}
